import { getApiEnvironmentTable } from '~/components/ContentTools/apiSkillEnvironments';

const EXPERIENCE_API_ENDPOINTS = [
  {
    label: 'Curated offer',
    method: 'GET',
    path: '/api/experiences/offers/:experienceId?currency=AUD&brand=:brand&curationData=true&disableCache=true',
    notes: 'Current curated experience offer data shown in Admin. Use this before editing.',
  },
  {
    label: 'Update curated offer',
    method: 'PATCH',
    path: '/api/experiences/offers/:experienceId?currency=AUD&brand=:brand',
    notes: 'Applies a partial curation payload for editable experience fields.',
  },
  {
    label: 'Original provider offer',
    method: 'GET',
    path: '/api/experiences/offers/:experienceId/original?brand=:brand',
    notes: 'Provider-sourced original content for comparison on Rezdy experiences.',
  },
  {
    label: 'Curation feature manager',
    method: 'GET',
    path: '/api/experiences/offers/:experienceId/curation-feature-manager',
    notes: 'Feature flags that indicate which fields are visible or editable.',
  },
  {
    label: 'Categories',
    method: 'GET',
    path: '/api/experiences/categories?currency=AUD&brand=:brand',
    notes: 'Available categories used when editing the experience category list.',
  },
  {
    label: 'Available dates',
    method: 'GET',
    path: '/api/experiences/offers/:experienceId/availability?brand=:brand',
    notes: 'Availability dates for ticket and booking checks.',
  },
  {
    label: 'Available times',
    method: 'GET',
    path: '/api/experiences/offers/:experienceId/availability/:day?brand=:brand&curationData=true',
    notes: 'Availability times for a selected day.',
  },
];

const EXPERIENCE_LIST_ORDER_ENDPOINTS = [
  {
    label: 'Current manual list order',
    method: 'GET',
    path: '/api/search/admin/v1/list-order/:listName?brand=:brand&region=:region',
    notes: 'Manual pinned offer order for a product or location experience list.',
  },
  {
    label: 'Save manual list order',
    method: 'PUT',
    path: '/api/search/admin/v1/list-order/:listName',
    notes: 'Replaces the manual pinned offer order for the selected list, brand, and region.',
  },
  {
    label: 'Search preview list',
    method: 'GET',
    path: '/api/search/unified/v1/list?brand=:brand&region=:region&placeIds=le_d41d8cd98f00b204e9800998ecf8427e&offerTypes=flash%2Calways_on%2Cchannel_manager&limit=50',
    notes: 'Algorithmic fallback preview used by Admin after manual list items.',
  },
];

const EXPERIENCE_LIST_ORDER_OPTIONS = [
  {
    label: 'Product: Experiences',
    listName: 'vertical-experience',
    notes: 'Main Experiences product list.',
  },
  {
    label: 'Location: Experiences Sydney',
    listName: 'vertical-experience-sydney',
    notes: 'Sydney location experience list.',
  },
  {
    label: 'Location: Experiences Melbourne',
    listName: 'vertical-experience-melbourne',
    notes: 'Melbourne location experience list.',
  },
  {
    label: 'Location: Experiences Brisbane',
    listName: 'vertical-experience-brisbane',
    notes: 'Brisbane location experience list.',
  },
  {
    label: 'Location: Experiences Adelaide',
    listName: 'vertical-experience-adelaide',
    notes: 'Adelaide location experience list.',
  },
  {
    label: 'Location: Experiences Perth',
    listName: 'vertical-experience-perth',
    notes: 'Perth location experience list.',
  },
];

const EXPERIENCE_PATCH_AREAS = [
  {
    area: 'Experience tab',
    fields: '`title`, `location`, `dealDescription`, `shortDescription`, `included`, `images`, `video`',
  },
  {
    area: 'Additional information',
    fields: '`thingsToKnowNew`, `cancellationInfo`, `vendorBookingCutoff`, `allowBuyNowBookLater`, `allowGiftCard`',
  },
  {
    area: 'Deal options',
    fields: '`tickets`, `bookingOptions`, `bookingFields`, `features`, `ticketUnitLabel`, `ticketed`',
  },
  {
    area: 'Pricing and discounts',
    fields:
      '`baseSalesPrice`, `margin`, `commissionMargin`, `bundleMinMarkupPct`, `memberDiscountEnabled`, `memberDiscountOverrides`',
  },
  {
    area: 'Categories and audiences',
    fields: '`categories`, `targetAudiences`',
  },
  {
    area: 'Curation options and schedule',
    fields:
      '`curationStatus`, `curationComment`, `isFlash`, `showPostPurchase`, `searchEngineMarketingEnabled`, `unlisted`, `showInJourneys`, `disableReviews`, `idSalesforceExternal`, `brands`, `brandSchedules`',
  },
];

export function getExperiencesCodexSkillText() {
  const environmentTable = getApiEnvironmentTable();
  const endpointTable = EXPERIENCE_API_ENDPOINTS.map(
    ({ label, method, path, notes }) => `| ${label} | \`${method}\` | \`${path}\` | ${notes} |`,
  ).join('\n');
  const listOrderEndpointTable = EXPERIENCE_LIST_ORDER_ENDPOINTS.map(
    ({ label, method, path, notes }) => `| ${label} | \`${method}\` | \`${path}\` | ${notes} |`,
  ).join('\n');
  const listOrderOptionsTable = EXPERIENCE_LIST_ORDER_OPTIONS.map(
    ({ label, listName, notes }) => `| ${label} | \`${listName}\` | ${notes} |`,
  ).join('\n');
  const patchAreasTable = EXPERIENCE_PATCH_AREAS.map(({ area, fields }) => `| ${area} | ${fields} |`).join('\n');

  return `# Luxury Escapes Admin Experiences API Editing Skill

Use this skill when a Luxury Escapes content user asks Codex to inspect or edit an experience through the Admin Portal API instead of navigating the browser UI.

## Inputs to ask the user for

- The API details block copied from the Admin Portal. It should include \`API_BASE_URL\` and \`ADMIN_BEARER_TOKEN\`.
- Which environment they want edited: \`test\` or \`prod\`.
- The experience ID, usually from an Admin URL like \`/experience/:experienceId\`.
- For list-order curation, the list label or list name, region code, brand, and the experience offer IDs to add, remove, or reorder.
- The brand to edit, usually the Admin tenant brand such as \`luxuryescapes\`.
- The exact content change requested.

Never ask the user to paste long-lived credentials. Never print the bearer token back to the user. Keep it only in memory for the current task.

## API hosts

Do not call the Admin Portal web-app host, such as \`https://test-admin.luxuryescapes.com\` or \`https://admin.luxuryescapes.com\`. The Admin Portal talks to a separate API host. Prefer the copied \`API_BASE_URL\` value from the Admin Portal. If the user did not paste API details, choose the API base URL from the environment:

| Environment | API base URL | Use for |
| --- | --- | --- |
${environmentTable}

If the user is working from a review app or a local setup with a custom backend, ask them to use the Admin Portal's "Copy API details" button so you get the exact configured \`window.configs.API_HOST\` value and a matching token.

## Authentication

Write \`ADMIN_BEARER_TOKEN\` to a file once (as in the copied API details), then load it with \`$(cat /tmp/le-admin-bearer.token)\` for every request. Do not paste the JWT into curl \`-H\` headers. Send every API request with:

\`\`\`http
Authorization: Bearer <ADMIN_TOKEN>
Content-Type: application/json;charset=UTF-8
\`\`\`

## Endpoints

The base URL for requests is:

\`\`\`text
<API_BASE_URL><URL path above>
\`\`\`

Replace \`:experienceId\`, \`:brand\`, and \`:day\` with the values for the task.

| Area | Method | URL path | Use for |
| --- | --- | --- | --- |
${endpointTable}

### Product and location experience list-order curation

Use these endpoints when the user asks to curate the manual order shown in Admin under \`/curation/offer-list-order\` for \`Product: Experiences\` or \`Location: Experiences ...\` lists. These lists affect search and homepage ranking by pinning selected experience offer IDs before the algorithmic fallback fills the rest of the list.

| Area | Method | URL path | Use for |
| --- | --- | --- | --- |
${listOrderEndpointTable}

Experience list options:

| Admin list label | \`listName\` | Notes |
| --- | --- | --- |
${listOrderOptionsTable}

The list-order \`PUT\` body replaces the full manual list for the selected list, brand, and region:

\`\`\`json
{
  "brand": "luxuryescapes",
  "region": "AU",
  "offers": [
    {
      "vertical": "experience",
      "bk": "rezdy:12345",
      "offerType": "always_on"
    }
  ]
}
\`\`\`

Each experience entry should use \`vertical: "experience"\`, \`bk\` equal to the experience offer ID, and \`offerType\` copied from the experience offer's current data. Experience offer IDs usually contain a colon, such as a provider prefix and provider offer ID.

If a request unexpectedly returns the Admin HTML shell instead of JSON, the host is wrong; switch from the Admin Portal web-app host to the API base URL for that environment.

## Editable PATCH fields

The update endpoint accepts a partial curation payload. Fetch the current curated offer first, then send only the fields needed for the requested change unless preserving nested arrays or objects requires including the full edited value for that field.

| Admin area | Common PATCH fields |
| --- | --- |
${patchAreasTable}

## Workflow

1. GET the curated offer with \`curationData=true\` and inspect the returned \`result\`.
2. If field visibility or editability is uncertain, GET the curation feature manager before editing.
3. Locate the smallest PATCH field or nested object that contains the requested content.
4. Make a minimal JSON edit. Preserve unrelated IDs, ordering, booleans, arrays, money objects, ticket modes, booking fields, and provider data.
5. Show the user a concise before/after summary before PATCHing, unless they already asked you to apply directly.
6. PATCH the partial payload to \`/api/experiences/offers/:experienceId?currency=AUD&brand=:brand\`.
7. GET the curated offer again with \`curationData=true\` and verify the expected content is present.
8. If the user asks to test a change and revert it, capture the original value first, PATCH the test value, verify, then PATCH the original value back and verify again.

## Product and location experience list-order workflow

Use this workflow for requests like "add these experience IDs to Product: Experiences", "move this offer above that one", "remove this offer from Experiences Sydney", or "curate the Melbourne experience order".

1. Confirm the target \`listName\`, \`brand\`, and \`region\`. Use \`vertical-experience\` for \`Product: Experiences\`; use the matching \`vertical-experience-<city>\` key for location lists.
2. GET \`/api/search/admin/v1/list-order/:listName?brand=:brand&region=:region\` and keep the returned \`result.offers\` as the source of truth.
3. For every new experience offer ID, GET \`/api/experiences/offers/:experienceId?currency=AUD&brand=:brand&curationData=true&disableCache=true\` and copy \`result.id\` and \`result.offerType\`.
4. Build the full replacement \`offers\` array in the exact desired order. Preserve every unrelated existing item and only add, remove, or reorder the targeted IDs. Avoid duplicates by comparing \`bk\`.
5. Before saving, show the user a concise before/after summary with the affected list, region, brand, moved IDs, removed IDs, and inserted IDs unless they already asked you to apply directly.
6. PUT the full replacement body to \`/api/search/admin/v1/list-order/:listName\`.
7. GET the same list-order endpoint again and verify the returned \`result.offers\` has the expected order.
8. If the user asks to apply an experience list to additional white-label brands, repeat the same PUT for only the explicitly requested extra brands. Do not replicate across brands implicitly.

The Admin UI may also show an algorithmic preview from \`/api/search/unified/v1/list\`. This preview is not saved by the list-order endpoint; it only shows what search may use after the manual pinned offers.

## Safety rules

- Prefer GET before PATCH. Do not construct payloads from memory.
- For list-order edits, prefer GET before PUT and always PUT the complete manual \`offers\` array, not a partial diff.
- Do not modify images, prices, tickets, availability, booking fields, schedules, or provider IDs unless the user specifically asks.
- Do not change non-experience product lists or regional home lists from this skill unless the user explicitly asks and understands this is outside experience curation.
- Do not replicate an experience list order to other regions or brands unless the user specifically names those targets.
- When editing arrays such as \`tickets\`, \`images\`, \`categories\`, \`targetAudiences\`, or \`brandSchedules\`, preserve all existing entries and only change the targeted item.
- Do not copy fields from the original provider offer over curated content unless the user explicitly asks.
- If the schema, feature manager, or current data does not clearly identify the target field, stop and ask for the exact field or tab instead of guessing.
- If a PATCH fails, report the status and response body without retrying destructive changes blindly.
`;
}
